/**
 * AuthErrorHandler - Handles authentication-specific errors
 */

import { HttpError } from '../../../core/services/HttpService';
import dialogUtil from '../../../shared/utils/dialog';

export class AuthErrorHandler {
  private static instance: AuthErrorHandler;

  static getInstance(): AuthErrorHandler {
    if (!AuthErrorHandler.instance) {
      AuthErrorHandler.instance = new AuthErrorHandler();
    }
    return AuthErrorHandler.instance;
  }

  /**
   * Handle login errors
   */
  handleLoginError(error: HttpError): boolean {
    switch (error.status) {
      case 400:
        this.handleLoginValidationError(error);
        return true;

      case 401:
        this.handleInvalidCredentials(error);
        return true;

      case 403:
        this.handleAccountDisabled(error);
        return true;

      case 429:
        this.handleTooManyAttempts(error);
        return true;

      default:
        // Let global handler deal with other errors
        return false;
    }
  }

  /**
   * Handle registration errors
   */
  handleRegistrationError(error: HttpError): boolean {
    switch (error.status) {
      case 400:
        this.handleRegistrationValidationError(error);
        return true;

      case 409:
        this.handleUserAlreadyExists(error);
        return true;

      case 422:
        this.handleRegistrationBusinessRuleError(error);
        return true;

      default:
        // Let global handler deal with other errors
        return false;
    }
  }

  // Private error handling methods for login
  private handleLoginValidationError(error: HttpError): void {
    const detail = this.extractErrorDetail(error);
    
    if (detail.includes('username') || detail.includes('email')) {
      dialogUtil.showToast({
        title: 'Invalid Credentials',
        message: 'Please enter a valid email address',
        type: 'error'
      });
    } else if (detail.includes('password')) {
      dialogUtil.showToast({
        title: 'Invalid Credentials',
        message: 'Password is required',
        type: 'error'
      });
    } else {
      dialogUtil.showToast({
        title: 'Login Error',
        message: detail || 'Please check your login credentials',
        type: 'error'
      });
    }
  }

  private handleInvalidCredentials(error: HttpError): void {
    dialogUtil.showToast({
      title: 'Login Failed',
      message: 'Invalid email or password. Please try again.',
      type: 'error'
    });
  }

  private handleAccountDisabled(error: HttpError): void {
    const detail = this.extractErrorDetail(error);
    
    dialogUtil.showModal({
      title: 'Account Disabled',
      content: detail || 'Your account has been disabled. Please contact support for assistance.',
      variant: 'warning',
      closeOnBackdropPress: true,
    });
  }

  private handleTooManyAttempts(error: HttpError): void {
    const detail = this.extractErrorDetail(error);
    
    dialogUtil.showModal({
      title: 'Too Many Login Attempts',
      content: detail || 'Too many login attempts. Please wait a few minutes before trying again.',
      variant: 'warning',
      closeOnBackdropPress: true,
    });
  }

  // Private error handling methods for registration
  private handleRegistrationValidationError(error: HttpError): void {
    const errors = error.data?.errors || error.data || {};
    
    if (typeof errors === 'object') {
      const errorMessages = [];
      
      if (errors.email) {
        errorMessages.push('Invalid email address');
      }
      if (errors.password) {
        errorMessages.push('Password does not meet requirements');
      }
      if (errors.password2) {
        errorMessages.push('Password confirmation does not match');
      }
      if (errors.first_name) {
        errorMessages.push('First name is required');
      }
      if (errors.last_name) {
        errorMessages.push('Last name is required');
      }
      
      const message = errorMessages.length > 0 
        ? errorMessages.join(', ')
        : this.extractErrorDetail(error) || 'Please check your registration information';
      
      dialogUtil.showToast({
        title: 'Registration Error',
        message: message,
        type: 'error'
      });
    } else {
      dialogUtil.showToast({
        title: 'Registration Error',
        message: this.extractErrorDetail(error) || 'Please check your registration information',
        type: 'error'
      });
    }
  }

  private handleUserAlreadyExists(error: HttpError): void {
    dialogUtil.showToast({
      title: 'Registration Failed',
      message: 'An account with this email address already exists. Please use a different email or try logging in.',
      type: 'error'
    });
  }

  private handleRegistrationBusinessRuleError(error: HttpError): void {
    const detail = this.extractErrorDetail(error);
    
    dialogUtil.showModal({
      title: 'Registration Not Allowed',
      content: detail || 'Registration is currently not available. Please contact support.',
      variant: 'warning',
      closeOnBackdropPress: true,
    });
  }

  /**
   * Extract meaningful error message from error response
   */
  private extractErrorDetail(error: HttpError): string {
    if (error.data?.detail) {
      return error.data.detail;
    }
    
    if (error.data && typeof error.data === 'object') {
      // Handle field-specific errors
      const fieldErrors = Object.values(error.data).flat();
      if (fieldErrors.length > 0) {
        return fieldErrors.join(', ');
      }
    }
    
    return '';
  }
}

// Export singleton instance
const authErrorHandler = AuthErrorHandler.getInstance();
export default authErrorHandler; 